/**
 * Dashboard Page.
 *
 * Landing page after login. Features time-aware greeting with gradient
 * text, KPI cards with colored glow accents and animated count-up numbers,
 * and quick action cards with gradient hover effects.
 */

import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Package,
  FileText,
  Wrench,
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Plus,
  ScanLine,
  BarChart3,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { useDashboardKPIs } from "@/features/sales/api";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: kpis, isLoading, isError } = useDashboardKPIs();
  const { data: alerts } = useQuery<
    Array<{
      type: string;
      severity: string;
      message: string;
      asset_id?: string;
      account_id?: string;
      recommended_action: string;
    }>
  >({
    queryKey: ["action-alerts"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/insights/action-alerts")).data,
  });

  const fmt = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(v);

  const fmtCompact = (v: number) =>
    new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(v);

  // Time-of-day greeting.
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  }, []);

  const firstName = user?.full_name?.split(" ")[0] ?? "there";

  return (
    <div className="page-wide space-y-8">
      {/* ── Hero Header with Aurora Mesh ── */}
      <div className="aurora-mesh rounded-[var(--radius-xl)] p-8 relative">
        <div className="relative z-10">
          <h2 className="text-3xl font-bold">
            <span className="text-gradient-brand">{greeting}</span>
            <span className="text-[var(--text-primary)]">, {firstName}</span>
          </h2>
          <p className="mt-2 text-sm text-[var(--text-secondary)] max-w-lg">
            Here&apos;s your business at a glance. Track inventory, monitor
            orders, and stay on top of everything that matters.
          </p>
        </div>
      </div>

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : isError ? (
        <Card>
          <p className="text-sm text-[var(--text-danger)]">
            Unable to load dashboard data. Make sure the backend is running.
          </p>
        </Card>
      ) : kpis ? (
        <>
          {/* ── KPI Cards ── */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 stagger-children">
            {/* Total Inventory */}
            <Link to="/inventory">
              <Card
                variant="gradient"
                className="metric-card metric-card-blue hover-glow cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                      Total Inventory
                    </p>
                    <p className="mt-2 text-3xl font-bold text-[var(--text-primary)] animate-count-up tabular-nums">
                      {kpis.total_inventory}
                    </p>
                    <p className="mt-1.5 text-xs text-[var(--text-secondary)]">
                      Value:{" "}
                      <span className="font-semibold">
                        {fmtCompact(kpis.total_inventory_value)}
                      </span>
                    </p>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 text-white shadow-md shadow-blue-500/25">
                    <Package className="h-5 w-5" />
                  </div>
                </div>
              </Card>
            </Link>

            {/* Open POs */}
            <Link to="/procurement">
              <Card
                variant="gradient"
                className="metric-card metric-card-amber hover-glow cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                      Open POs
                    </p>
                    <p className="mt-2 text-3xl font-bold text-[var(--text-primary)] animate-count-up tabular-nums">
                      {kpis.open_purchase_orders}
                    </p>
                    <p className="mt-1.5 text-xs text-[var(--text-secondary)]">
                      Draft, Submitted, or In-Transit
                    </p>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-md shadow-amber-500/25">
                    <FileText className="h-5 w-5" />
                  </div>
                </div>
              </Card>
            </Link>

            {/* Active Work Orders */}
            <Link to="/shop">
              <Card
                variant="gradient"
                className="metric-card metric-card-violet hover-glow cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                      Active Work Orders
                    </p>
                    <p className="mt-2 text-3xl font-bold text-[var(--text-primary)] animate-count-up tabular-nums">
                      {kpis.active_work_orders}
                    </p>
                    <p className="mt-1.5 text-xs text-[var(--text-secondary)]">
                      Open or In Progress
                    </p>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-400 to-purple-600 text-white shadow-md shadow-violet-500/25">
                    <Wrench className="h-5 w-5" />
                  </div>
                </div>
              </Card>
            </Link>

            {/* Monthly Sales */}
            <Link to="/sales">
              <Card
                variant="gradient"
                className="metric-card metric-card-green hover-glow cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                      Monthly Sales
                    </p>
                    <p className="mt-2 text-3xl font-bold text-gradient-success animate-count-up tabular-nums">
                      {fmt(kpis.monthly_sales_revenue)}
                    </p>
                    <div className="mt-1.5 flex items-center gap-1">
                      {kpis.monthly_sales_count > 0 ? (
                        <TrendingUp className="h-3 w-3 text-[var(--text-success)]" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-[var(--text-tertiary)]" />
                      )}
                      <p className="text-xs text-[var(--text-secondary)]">
                        {kpis.monthly_sales_count} order
                        {kpis.monthly_sales_count !== 1 ? "s" : ""} this month
                      </p>
                    </div>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-400 to-green-600 text-white shadow-md shadow-emerald-500/25">
                    <ShoppingCart className="h-5 w-5" />
                  </div>
                </div>
              </Card>
            </Link>
          </div>

          {/* ── Inventory Health ── */}
          {(kpis.avg_days_in_inventory > 0 || kpis.action_alert_count > 0) && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Card padding="sm" className="metric-card">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-sky-400 to-sky-600 text-white shadow-sm shadow-sky-500/20">
                    <Clock className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-xs text-[var(--text-tertiary)]">
                      Avg Days in Inventory
                    </p>
                    <p className="text-lg font-bold text-[var(--text-primary)]">
                      {kpis.avg_days_in_inventory}
                    </p>
                  </div>
                </div>
              </Card>
              {kpis.action_alert_count > 0 && (
                <Link to="/reports?tab=insights">
                  <Card
                    padding="sm"
                    className="metric-card group cursor-pointer hover-lift"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-red-400 to-red-600 text-white shadow-sm shadow-red-500/20">
                        <AlertTriangle className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-tertiary)]">
                          Action Alerts
                        </p>
                        <p className="text-lg font-bold text-[var(--text-danger)]">
                          {kpis.action_alert_count}
                        </p>
                      </div>
                    </div>
                  </Card>
                </Link>
              )}
            </div>
          )}

          {/* ── Top Alerts ── */}
          {alerts && alerts.length > 0 && (
            <Card padding="sm">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-3">
                Priority Alerts
              </h3>
              <div className="space-y-2">
                {alerts.slice(0, 5).map((alert, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 rounded-[var(--radius-md)] bg-[var(--surface-sunken)] px-3 py-2"
                  >
                    <Badge
                      variant={
                        alert.severity === "critical"
                          ? "danger"
                          : alert.severity === "warning"
                            ? "warning"
                            : "info"
                      }
                    >
                      {alert.severity}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[var(--text-primary)]">
                        {alert.message}
                      </p>
                      <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
                        {alert.recommended_action}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* ── Quick Actions ── */}
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 stagger-children">
              {[
                {
                  label: "Add Asset",
                  href: "/inventory/new",
                  icon: Plus,
                  color:
                    "bg-gradient-to-br from-blue-400 to-blue-600 text-white shadow-sm shadow-blue-500/20",
                },
                {
                  label: "Create PO",
                  href: "#",
                  onClick: async () => {
                    try {
                      const resp = await apiClient.post(
                        "/api/v1/purchase-orders",
                        {},
                      );
                      navigate(`/purchase-orders/${resp.data.id}`);
                    } catch {
                      toast.error("Failed to create purchase order");
                    }
                  },
                  icon: FileText,
                  color:
                    "bg-gradient-to-br from-violet-400 to-purple-600 text-white shadow-sm shadow-violet-500/20",
                },
                {
                  label: "New Work Order",
                  href: "/work-orders/new",
                  icon: Wrench,
                  color:
                    "bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm shadow-amber-500/20",
                },
                {
                  label: "New Sale",
                  href: "/sales/new",
                  icon: ShoppingCart,
                  color:
                    "bg-gradient-to-br from-emerald-400 to-green-600 text-white shadow-sm shadow-emerald-500/20",
                },
              ].map((action) => {
                const cardContent = (
                  <Card
                    variant="glow"
                    padding="sm"
                    className="group hover-gradient"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-9 w-9 items-center justify-center rounded-lg ${action.color} transition-transform duration-200 group-hover:scale-110`}
                      >
                        <action.icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[var(--text-primary)]">
                          {action.label}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-[var(--text-tertiary)] opacity-0 -translate-x-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0" />
                    </div>
                  </Card>
                );

                if ("onClick" in action && action.onClick) {
                  return (
                    <button
                      key={action.label}
                      onClick={action.onClick}
                      className="text-left"
                    >
                      {cardContent}
                    </button>
                  );
                }

                return (
                  <Link key={action.label} to={action.href}>
                    {cardContent}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* ── Secondary Actions ── */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Link to="/inventory?tab=yard-map">
              <Card
                variant="glow"
                padding="sm"
                className="group hover-gradient"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-teal-400 to-teal-600 text-white shadow-sm shadow-teal-500/20">
                    <ScanLine className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[var(--text-primary)]">
                      Facility Map
                    </p>
                    <p className="text-xs text-[var(--text-tertiary)]">
                      Visual inventory locations
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 ml-auto text-[var(--text-tertiary)] opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
              </Card>
            </Link>
            <Link to="/reports">
              <Card
                variant="glow"
                padding="sm"
                className="group hover-gradient"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-rose-400 to-rose-600 text-white shadow-sm shadow-rose-500/20">
                    <BarChart3 className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[var(--text-primary)]">
                      Analytics & Reports
                    </p>
                    <p className="text-xs text-[var(--text-tertiary)]">
                      Insights and performance metrics
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 ml-auto text-[var(--text-tertiary)] opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
              </Card>
            </Link>
          </div>
        </>
      ) : null}
    </div>
  );
}

export default DashboardPage;
